import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

// Colore rosa/rosso fisso per i cerchi del cursore (colore del gradiente)
const CURSOR_COLOR = '#ff6b6b'

// Breakpoint per mobile (standard Tailwind md)
const MOBILE_BREAKPOINT = 768

export function CustomCursor() {
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isHovering, setIsHovering] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Rileva se siamo su mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  // Tracciamento mouse - solo su desktop
  useEffect(() => {
    if (isMobile) return // Non aggiungere listener su mobile

    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY })
      setIsVisible(true)
    }

    const handleMouseLeave = () => {
      setIsVisible(false)
    }

    window.addEventListener('mousemove', handleMouseMove)
    document.body.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      document.body.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [isMobile])

  // Rileva elementi interattivi - solo su desktop
  useEffect(() => {
    if (isMobile) return

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      
      const isButton = target.tagName === 'BUTTON' || !!target.closest('button')
      const isLink = target.tagName === 'A' || !!target.closest('a')
      const isCursorPointer = target.classList.contains('cursor-pointer') || !!target.closest('.cursor-pointer')
      
      setIsHovering(isButton || isLink || isCursorPointer)
    }

    document.addEventListener('mouseover', handleMouseOver)
    return () => document.removeEventListener('mouseover', handleMouseOver)
  }, [isMobile])
  
  // Dimensione normale: 20px, quando interagisce: 300% più grande = 80px
  const normalSize = 20
  const hoverSize = 80

  // Su mobile non renderizziamo nulla - usa il cursore di sistema
  if (isMobile) {
    return null
  }

  return (
    <>
      {/* Cursore normale (puntino centrale) */}
      <motion.div
        className="fixed pointer-events-none z-[99999] rounded-full"
        animate={{
          x: position.x,
          y: position.y,
          opacity: isVisible ? 1 : 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 28,
          mass: 0.5
        }}
        style={{
          width: 6,
          height: 6,
          backgroundColor: CURSOR_COLOR,
          translateX: '-50%',
          translateY: '-50%',
        }}
      />

      {/* Sfera che appare quando interagisce */}
      <AnimatePresence>
        {isHovering && (
          <motion.div
            className="fixed pointer-events-none z-[99998] rounded-full flex items-center justify-center"
            initial={{ 
              width: normalSize, 
              height: normalSize, 
              opacity: 0,
              scale: 0.5
            }}
            animate={{ 
              width: hoverSize, 
              height: hoverSize, 
              opacity: 1,
              scale: 1,
              x: position.x,
              y: position.y,
            }}
            exit={{ 
              width: normalSize, 
              height: normalSize, 
              opacity: 0,
              scale: 0.5
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 25,
              mass: 0.8
            }}
            style={{
              backgroundColor: `${CURSOR_COLOR}40`,
              border: `2px solid ${CURSOR_COLOR}`,
              translateX: '-50%',
              translateY: '-50%',
            }}
          >
          </motion.div>
        )}
      </AnimatePresence>

      {/* Anello esterno che segue con delay */}
      <motion.div
        className="fixed pointer-events-none z-[99997] rounded-full border-2"
        animate={{
          x: position.x,
          y: position.y,
          width: isHovering ? hoverSize + 20 : normalSize + 10,
          height: isHovering ? hoverSize + 20 : normalSize + 10,
          opacity: isVisible ? (isHovering ? 0.6 : 0.3) : 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 150,
          damping: 20,
          mass: 1
        }}
        style={{
          borderColor: CURSOR_COLOR,
          translateX: '-50%',
          translateY: '-50%',
        }}
      />
    </>
  )
}
